import type { EntityType, EventOption } from "../../types";
import { featuresForType } from "../../config/entityFeatures";
import { detectFeatures } from "../../utils/entityFeatures";

interface EntityFeatureTogglesProps {
  type: EntityType;
  options: EventOption[];
  onToggle: (featureId: string, on: boolean) => void;
}

/**
 * What this place has, as things you'd say about it rather than steps you'd
 * log: a preshow, lockers, a front desk. Ticking one adds the steps it brings
 * and wires them in; unticking takes them back out.
 *
 * This sits above the step list because it's the answer for almost every
 * entity — of the rides carried over from the old database, 90 of 92 are
 * exactly the core plus some combination of these. The step list underneath
 * stays for the two that aren't, and for anything nobody thought of.
 */
export function EntityFeatureToggles({ type, options, onToggle }: EntityFeatureTogglesProps) {
  const features = featuresForType(type);
  if (features.length === 0) return null;

  const on = new Set(detectFeatures(options, type));
  const following = options.length === 0;

  return (
    <div className="entity-features">
      <span className="form-label">What does it have?</span>
      <span className="form-hint">
        {following
          ? "Following the defaults for this type. Changing anything here gives it steps of its own."
          : "Ticking one adds the steps it brings; unticking takes them out again."}
      </span>
      <div className="entity-feature-list">
        {features.map((feature) => {
          const checked = on.has(feature.id);
          return (
            <label
              key={feature.id}
              className={`entity-feature${checked ? " is-on" : ""}`}
              title={feature.hint}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onToggle(feature.id, e.target.checked)}
              />
              <span className="entity-feature-label">{feature.label}</span>
              <span className="entity-feature-hint">{feature.hint}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
